import { format_price } from "../../utils/cart-utils";
import productFormFormatMixin from "../mixins/product-form-format-mixin";

////////////////////
// Template
////////////////////
const template = /*html*/ `

<form class="c-product-form" ref="form" @submit="onSubmit">
    <div class="c-product-form_inner">
        <div class="c-product-form_tag">
            <span v-if="isNew.trim() == 'true' && !isOutOfStock" class="c-tag">
                <span class="c-tag_label">
                    <slot name="new"></slot>
                </span>
            </span>
            <span v-if="!isCurrentVariantExist" class="c-tag -warning">
                <span class="c-tag_label">
                    <slot name="not-available"></slot>
                </span>
            </span>
            <span v-if="isOutOfStock" class="c-tag -warning">
                <span class="c-tag_label">
                    <slot name="out-of-stock"></slot>
                </span>
            </span>
            <span v-if="isQuantityNotAvailable" class="c-tag -warning">
                <span class="c-tag_label">
                    <slot name="quantity-not-available"></slot>
                </span>
            </span>
            <span v-if="isSale && !isOutOfStock" class="c-tag -sale">
                <span class="c-tag_label">
                    <slot name="sale"></slot>
                </span>
            </span>

            <!-- Custom Tags -->
            <slot name="tags"></slot>
        </div>

        <!-- Title -->
        <h1 class="c-product-form_title || c-heading -h2">
            <slot name="title"></slot>
        </h1>

        <!-- Description -->
        <div class="c-product-form_description || c-wysiwyg">
            <slot name="description"></slot>
        </div>

        <div class="c-product-form_shipping || c-shipping-mention" v-if="hasShippingSlot">
            <div class="c-shipping-mention_inner">
                <p><slot name="shipping"></slot><sup>*</sup></p>
            </div>
        </div>

        <div class="c-product-form_table">
            <!-- Variants selectors -->
            <div class="c-product-form_selector || c-product-form_table_row" v-for="(option, index) in options" :key="index" v-if="options.length && options[0].values && options[0].values.length > 1">
                <product-form-selector
                    v-if="option && option.values && option.values.length > 1"
                    :label="option.name"
                    :values="option.values"
                    :current-value="currentOptions[index]"
                    :position="index"
                    @onSelect="onOptionChange"
                />
            </div>

            <!-- Quantity input -->
            <div class="c-product-form_quantity || c-product-form_table_row">
                <div class="c-product-form_quantity || c-product-form_table_row_label">
                    <slot name="quantity"></slot>
                </div>

                <quantity-input class="c-product-form_add_item" ref="quantityInput" :max="currentVariantItem && currentVariantItem.quantity" @onUpdate="onUpdateQuantity"/>
            </div>

            <!-- Price -->
            <div class="c-product-form_price || c-product-form_table_row">
                <div class="c-product-form_price_label || c-product-form_table_row_label">
                    <slot name="price"></slot>
                </div>

                <!-- Price before discount -->
                <p class="c-product-form_price_original || c-text -body" v-if="currentOriginalVariantPrice">
                    {{ currentOriginalVariantPrice }}
                </p>

                <p class="c-product-form_price_current || c-text -subtitle">
                    {{ currentVariantPrice }}
                </p>
            </div>

            <slot name="custom-input"></slot>

            <div class="c-product-form_add || c-product-form_table_row">
                <!-- Add to cart -->
                <button class="c-product-form_add_item || c-button -big -spinner" type="submit" :disabled="isNotAvailable">
                    <span class="c-button_label"><slot name="add-to-cart"></slot></span>
                    <span class="c-button_spinner">
                        <span class="c-button_spinner_item"></span>
                    </span>
                </button>
            </div>
        </div>
    </div>
</form>

`;


////////////////////
// Scripts
////////////////////
export default function VueProductForm(moduleInstance) {

    Vue.component("product-form", {

        template,

        mixins: [ productFormFormatMixin ],

        props: {
            variantsJson: {
                type: String,
                required: true
            },
            defaultCurrentVariantId: {
                type: String,
                required: true
            },
            optionsJson: {
                type: String,
                required: true
            },
            quantitiesJson: {
                type: String,
                required: true
            },
            colorsJson: {
                type: String,
                required: true
            },
            currencyIsoCode: {
                type: String,
                required: true
            },
            currencySymbol: {
                type: String,
                required: true
            },
            isNew: {
                type: Boolean,
                required: true
            },
            customTextLabel: {
                type: String,
                default: 'Champ personnalisé'
            },
        },

        data() {
            return {
                currentVariantItem: null,
                isCurrentVariantExist: true,
                quantity: 1,
                customInput: '',
            };
        },

        computed: {
            currentVariantName() {
                return this.isCurrentVariantExist ? this.currentVariantItem.optionsValues.join(' - ') : ''
            },

            currentVariantPrice() {
                return this.isCurrentVariantExist ? format_price(this.currentVariantItem.price, this.currencySymbol) : '-'
            },

            currentOriginalVariantPrice() {
                return this.isCurrentVariantExist && this.currentVariantItem.compare_at_price ? format_price(this.currentVariantItem.compare_at_price, this.currencySymbol) : ''
            },

            currentVariantQuantity() {
                return this.isCurrentVariantExist ? this.currentVariantItem.quantity : ''
            },

            currentVariantSale() {
                return this.isCurrentVariantExist && this.currentVariantItem.price && this.currentVariantItem.compare_at_price && this.currentVariantItem.price < this.currentVariantItem.compare_at_price  ? `-${100 - Math.round(this.currentVariantItem.price / this.currentVariantItem.compare_at_price * 100)}%` : ''
            },

            isNotAvailable() {
                return this.isOutOfStock || !this.isCurrentVariantExist || this.isQuantityNotAvailable
            },

            isOutOfStock() {
                return this.isCurrentVariantExist && !this.currentVariantItem.available
            },

            isQuantityNotAvailable() {
                return this.isCurrentVariantExist && this.currentVariantItem.available && this.quantity > this.currentVariantItem.quantity
            },

            isSale() {
                return this.isCurrentVariantExist && this.currentVariantItem.compare_at_price
            },
            hasShippingSlot() {
                return !!this.$slots.shipping;
            }
        },

        created() {
            this.variantItems = this.variantsJson ? JSON.parse(this.variantsJson) : []
            this.options = this.optionsJson ? JSON.parse(this.optionsJson) : []

            const currentVariantItemId = this.defaultCurrentVariantId ? JSON.parse(this.defaultCurrentVariantId) : null
            const quantities = this.quantitiesJson ? JSON.parse(this.quantitiesJson) : []
            const colors = this.colorsJson ? JSON.parse(this.colorsJson) : []

            // Update options
            this.formatOptions(this.options);

            // Update variantItems
            this.formatVariantsItems(this.variantItems, this.options, quantities, colors)

            // Update options colors
            this.formatOptionsColors(this.options, this.variantItems)

            // Set current product variant
            this.currentVariantItem = this.variantItems.find(variant => variant.id === currentVariantItemId)

            // Set current options
            this.currentOptions = this.currentVariantItem.options.map(option => option.value)
        },

        methods: {
            /////////////////
            // Callbacks
            /////////////////

            onOptionChange(newOption) {
                // Update new current options
                this.currentOptions[newOption.position] = newOption.value

                // Update current variant item
                this.updateCurrentVariant()

                // Update quantity status
                this.$nextTick(() => {
                    this.$refs.quantityInput.updateQuantityStatus()
                })

                // Log state
                // this.logProductFormState()
            },

            onUpdateQuantity(newQuantity) {
                // Update quantity
                this.quantity = newQuantity

                // Log state
                // this.logProductFormState()
            },

            onSubmit(e) {
                e.preventDefault();

                const cartItem = {
                    id: this.currentVariantItem.id,
                    quantity: this.quantity,
                    properties: {} // Initialize properties as an empty object
                };

                const formDataEl = new FormData(this.$refs.form);
                const customInput = formDataEl?.get('customInput');

                if (customInput) {
                    cartItem.properties[this.customTextLabel] = customInput;
                }

                const formData = {
                    items: [
                        cartItem
                    ],
                };

                const item = {
                    variant_id: this.currentVariantItem.id,
                    sku: this.currentVariantItem.sku,
                    title: this.currentVariantItem.name,
                    quantity: this.quantity,
                    price: this.currentVariantItem.price / 100,
                    url: window.location.href.includes('flambette.com') ? window.location.href : null
                };

                moduleInstance.call("addToCart", { formData: formData, openCart: true, item: item }, "Cart");
            },

            /////////////////
            // Methods
            /////////////////

            /**
            * Update current product variant and set exist state
            */
            updateCurrentVariant() {
                const currentOptionsId = this.currentOptions.join('')

                this.currentVariantItem = this.variantItems.find(variantItem => variantItem.optionsValues.join('') === currentOptionsId )

                if (!this.currentVariantItem) {
                    this.isCurrentVariantExist = false
                } else {
                    this.isCurrentVariantExist = true

                    // Update url
                    this.updateUrl(this.currentVariantItem.id)
                }

                if (this.currentVariantItem) {
                    // Update gallery
                    moduleInstance.call('onProductVariantChange', this.currentVariantItem.id, 'ProductGallery')
                }
            },

            /**
            * Update current url with variant parameter
            */
             updateUrl(variantId) {
                if (!variantId) return

                const url = new URL(window.location);
                url.searchParams.set('variant', variantId);
                window.history.replaceState({}, '', url);
            },

            /**
            * Log state
            */
            logProductFormState() {
                console.group('Product form state')
                    console.log('gid', this.currentVariantItem && this.currentVariantItem.gid)
                    console.log('currentItem', this.currentVariantItem)
                    console.log('currentOptions', this.currentOptions)
                    console.log('options', this.options)
                    console.log('quantity', this.quantity)
                    console.log('quantityMax', this.currentVariantItem && this.currentVariantItem.quantity)
                    console.log('currentVariantName', this.currentVariantName)
                    console.log('currentVariantPrice', this.currentVariantPrice)
                    console.log('currentOriginalVariantPrice', this.currentOriginalVariantPrice)
                    console.log('currentVariantSale', this.currentVariantSale)
                    console.log('isNotAvailable', this.isNotAvailable)
                    console.log('isOutOfStock', this.isOutOfStock)
                    console.log('isQuantityNotAvailable', this.isQuantityNotAvailable)
                console.groupEnd()
            }
        }
    });
}
